// src/admin/pages/tradein/TradeInManagement.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import CarRentalIcon from '@mui/icons-material/CarRental';
import { useAuth } from '../../../contexts/AuthContext';
import { TradeInService } from './tradeInService';
import { TradeInSubmission, TRADE_IN_STATUSES } from '../../../enums/tradeIn';
import TradeInTable from './TradeInTable';
import TradeInDrawer from './TradeInDrawer';
import ToastNotification from '../common/Toastnotification';

const ITEMS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const TradeInManagement: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile, hasPermission, isSuperAdmin } = useAuth();
  const userName = userProfile?.displayName || currentUser?.email || 'Admin';

  const canManage = isSuperAdmin || hasPermission('tradein.manage');

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // ── Data state ────────────────────────────────────────────────────────────
  const [items, setItems] = useState<TradeInSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  // ── UI state ──────────────────────────────────────────────────────────────
  const [editing, setEditing] = useState<TradeInSubmission | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TradeInSubmission | null>(null);
  const [deleteLoading, setDeleteLoading] = useState('');
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  // ── Search & filter state ────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const showToast = (message: string, severity: ToastState['severity']) =>
    setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast((t) => ({ ...t, open: false }));

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      setItems(await TradeInService.getAll());
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to fetch trade-in submissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── Stats (derived client-side — no dedicated stats endpoint) ─────────────
  const stats = useMemo(
    () => ({
      total: items.length,
      newSubmissions: items.filter((t) => t.status === 'New Submission').length,
      underReview: items.filter((t) => t.status === 'Under Review' || t.status === 'Offer Made').length,
      accepted: items.filter((t) => t.status === 'Accepted').length,
    }),
    [items]
  );

  // ── Filtering (client-side — dataset is bounded to admin-visible leads) ───
  const filtered = useMemo(() => {
    return items.filter((t) => {
      const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
      const q = searchQuery.trim().toLowerCase();
      const matchesSearch =
        !q ||
        t.ownerName?.toLowerCase().includes(q) ||
        t.ownerPhone?.toLowerCase().includes(q) ||
        `${t.carMake} ${t.carModel}`.toLowerCase().includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [items, statusFilter, searchQuery]);

  const totalFiltered = filtered.length;
  const totalPages = Math.ceil(totalFiltered / ITEMS_PER_PAGE);
  const pageItems = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const resetToFirstPage = () => setCurrentPage(1);
  const handlePageChange = (_e: React.ChangeEvent<unknown>, page: number) => setCurrentPage(page);

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    resetToFirstPage();
  };

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all';
  const activeFiltersCount = [searchQuery !== '', statusFilter !== 'all'].filter(Boolean).length;

  // ── Edit drawer ───────────────────────────────────────────────────────────
  const openEdit = (t: TradeInSubmission) => {
    setEditing(t);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setEditing(null);
  };

  const handleSave = async (formData: { status: string; offeredPrice: string; internalNotes: string; rejectionReason: string }) => {
    if (!editing?.id) return;
    try {
      await TradeInService.update(
        editing.id,
        {
          status: formData.status as any,
          offeredPrice: parseFloat(formData.offeredPrice) || 0,
          internalNotes: formData.internalNotes,
          rejectionReason: formData.status === 'Rejected' ? (formData.rejectionReason as any) : undefined,
        },
        currentUser?.uid || '',
        userName
      );
      handleDrawerClose();
      showToast(
        formData.status === 'Accepted' ? 'Trade-in accepted'
          : formData.status === 'Rejected' ? 'Trade-in rejected'
          : 'Trade-in submission updated successfully',
        'success'
      );
      await load();
    } catch (err: any) {
      showToast('Failed to update submission: ' + (err.message || 'Unknown error'), 'error');
    }
  };

  // ── Convert an accepted trade-in into a new Car Inventory listing ─────────
  // Hands off to the Cars page with the trade-in's known details pre-filled
  // into the Add Car drawer, closing the loop on `convertedToCarId` (see
  // enums/tradeIn.ts) — staff still review/complete the listing before saving.
  const handleCreateListing = (submission: TradeInSubmission) => {
    navigate('/admin/products', { state: { prefillFromTradeIn: submission } });
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id);
      await TradeInService.delete(deleteTarget.id, currentUser?.uid || '', userName);
      setDeleteTarget(null);
      showToast('Trade-in submission deleted successfully', 'success');
      resetToFirstPage();
      await load();
    } catch (err: any) {
      showToast('Failed to delete submission: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
    }
  };

  const handleRefresh = () => {
    load();
    showToast('Trade-in submissions refreshed', 'info');
  };

  // ── Initial full-page loading state ─────────────────────────────────────
  if (loading && items.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px" flexDirection="column">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading trade-in submissions...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <CarRentalIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Trade-In Submissions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cars customers have submitted for sale or trade-in evaluation
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button onClick={handleRefresh} variant="outlined" startIcon={<RefreshIcon />} disabled={loading}>
              Refresh
            </Button>
          </Stack>
        </Stack>

        {/* ── Error banner ── */}
        {errorMsg && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={handleRefresh} startIcon={<RefreshIcon />}>
                Retry
              </Button>
            }
          >
            {errorMsg}
          </Alert>
        )}

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
          {[
            { label: 'Total', value: stats.total },
            { label: 'New Submissions', value: stats.newSubmissions },
            { label: 'Under Review / Offer Made', value: stats.underReview },
            { label: 'Accepted', value: stats.accepted },
          ].map((s) => (
            <Grid item xs={6} sm={6} md={3} key={s.label} sx={{ display: 'flex' }}>
              <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                    flex: 1,
                  }}
                >
                  <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                    {s.label}
                  </Typography>
                  <Typography
                    variant="h5"
                    component="div"
                    color="text.primary"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' } }}
                  >
                    {s.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search by owner, phone, or car... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
                  Clear all
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => handleStatusFilterChange(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      {TRADE_IN_STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${totalFiltered} submissions matching your filters`
                        : `Showing ${totalFiltered} submissions`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: { xs: 'visible', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: 0 },
        }}
      >
        {/* ── Empty state ── */}
        {pageItems.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <CarRentalIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Submissions Match Your Filters' : 'No Trade-In Submissions Yet'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Trade-in submissions from the website will appear here.'}
                </Typography>
                {hasActiveFilters && (
                  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
                    Clear Filters
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* ── Table + Pagination ── */}
        {pageItems.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <Box
              ref={tableWrapperRef}
              sx={{ flex: 1, overflow: 'auto', mb: 2, minHeight: { xs: 'auto', md: 0 } }}
            >
              <TradeInTable
                items={pageItems}
                onEdit={openEdit}
                onDelete={setDeleteTarget}
                deleteLoading={deleteLoading}
                isDark={isDark}
                canManage={canManage}
              />
            </Box>

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalFiltered === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1}–
                    {Math.min(currentPage * ITEMS_PER_PAGE, totalFiltered)} of {totalFiltered} submissions
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Review Drawer ── */}
      <TradeInDrawer
        isOpen={isDrawerOpen}
        submission={editing}
        onClose={handleDrawerClose}
        onSave={handleSave}
        onCreateListing={handleCreateListing}
      />

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Submission</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete this trade-in submission from <strong>"{deleteTarget?.ownerName}"</strong>? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteDialogLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteDialogLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Toast ── */}
      <ToastNotification open={toast.open} message={toast.message} severity={toast.severity} onClose={handleCloseToast} />
    </Box>
  );
};

export default TradeInManagement;
